import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, firstValueFrom } from 'rxjs';
import { RUNTIME_CONFIG } from '../configuration/config';

export type DataMode = 'live' | 'demo';
export interface DataModeCapabilities {
  activeMode: DataMode;
  defaultMode: DataMode;
  allowedModes: DataMode[];
  demoAllowed: boolean;
  readOnly: boolean;
  referenceDate: string;
}

/** A local preference never grants access: the server resolves and authorizes every request. */
@Injectable({ providedIn: 'root' })
export class DataModeService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(RUNTIME_CONFIG);
  private readonly selected = signal<DataMode>('live');
  readonly activeMode = this.selected.asReadonly();
  readonly isDemoMode = computed(() => this.activeMode() === 'demo');
  readonly capabilities = signal<DataModeCapabilities | null>(null);
  readonly ready = signal(false);
  readonly error = signal('');
  readonly switching = signal(false);
  private readonly changed = new Subject<DataMode>();
  readonly changes = this.changed.asObservable();
  private readonly refreshers = new Set<() => void>();
  private preferenceKey = '';

  async initialize(userId: string): Promise<void> {
    const preferenceKey = `dkc-data-mode:${encodeURIComponent(this.config.entra.tenantId || 'local')}:${encodeURIComponent(userId)}`;
    if (this.preferenceKey && this.preferenceKey !== preferenceKey) {
      this.selected.set('live');
      this.changed.next('live');
      this.capabilities.set(null);
      for (const refresh of [...this.refreshers]) refresh();
    }
    this.preferenceKey = preferenceKey;
    try {
      const capabilities = await firstValueFrom(
        this.http.get<DataModeCapabilities>(`${this.config.apiBaseUrl}/data-mode/capabilities`),
      );
      this.capabilities.set(capabilities);
      const preference = localStorage.getItem(this.preferenceKey);
      if (preference === 'demo') {
        if (capabilities.demoAllowed && capabilities.allowedModes.includes('demo')) {
          this.setMode('demo');
        } else {
          this.error.set(
            'Your saved Demo selection is not permitted by this server. Live Data is selected.',
          );
        }
      }
    } catch {
      this.error.set(
        'Data source capabilities could not be verified. Demo selection is unavailable. Retry verification.',
      );
    } finally {
      this.ready.set(true);
    }
  }

  setMode(mode: DataMode): void {
    if (mode !== 'live' && mode !== 'demo') return;
    if (mode === this.activeMode()) return;
    if (
      mode === 'demo' &&
      (!this.capabilities()?.demoAllowed || !this.capabilities()?.allowedModes.includes('demo'))
    ) {
      this.error.set('Demo access is not enabled for this account and server.');
      return;
    }
    this.switching.set(true);
    this.error.set('');
    this.selected.set(mode);
    // Unsubscribe old HTTP work before issuing any reads for the newly selected source.
    this.changed.next(mode);
    if (this.preferenceKey) localStorage.setItem(this.preferenceKey, mode);
    for (const refresh of [...this.refreshers]) refresh();
    queueMicrotask(() => this.switching.set(false));
  }

  /** Call in a component injection context; registration is removed when its route is destroyed. */
  onChange(refresh: () => void): void {
    const destroyRef = inject(DestroyRef);
    this.refreshers.add(refresh);
    destroyRef.onDestroy(() => this.refreshers.delete(refresh));
  }
}
